import biLogger from 'web-bi-logger';
import _ from 'lodash';
import safeLocalStorage from './safe-local-storage';
import {getPageLoadTime, getPageRenderingTime} from './utils';

/**
 * This magic object is used in App Market 2.0. Keep it updated till App Market 2.0 is killed.
 */
function createAppEventStore() {
  const store = typeof window === 'object' ? window.parent : Object.create(null);
  store._appMarket = store._appMarket || Object.create(null);
  store._appMarket.appEventStore = store._appMarket.appEventStore || Object.create(null);

  _.defaults(store._appMarket.appEventStore, {
    urlParams: Object.create(null),
    addAppFlow: Object.create(null),
    vertical: undefined,
    personalized: 'general',
    lastModalTabId: 'overview',
    lastSearch: undefined,
    lastNonModalState: undefined,
    lastNonModalStateName: undefined,
    lastNonModalStateKey: undefined,
    lastNonModalStateNameOld: undefined,
    lastStateKey: undefined,
    lastAppClickedPosition: undefined,
    modalOpenedManually: false,
    pendingAppsCount: undefined,
    autocomplete: Object.create(null),
    autoCompleteQuery: undefined,
    marketOrigin: 'ed_home',
    market: 'editor',
    isCurrentNonModal: true,
    modalApp: Object.create(null),
    permissionsApp: Object.create(null)
  });

  return store._appMarket.appEventStore;
}

export const appEventStore = createAppEventStore();

let lastLocalStateKey, BI = {log: () => Promise.resolve()}, BIFactory;

export function updateBIFields(state, history) {
  const pageName = getPageName(history.pathname, state);
  const pageKey = getPageKey(history.pathname);

  lastLocalStateKey = pageKey;
  appEventStore.lastStateKey = pageKey;
  appEventStore.lastNonModalStateName = pageName ? pageName : appEventStore.lastNonModalStateName;
  appEventStore.lastNonModalStateKey = isModal(history.pathname) ? appEventStore.lastNonModalStateKey : pageKey;

  appEventStore.isCurrentNonModal = !isModal(history.pathname);
  appEventStore.urlParams = Object.assign({referralInfo: '', referralInfoCategory: ''}, history.query);
  appEventStore.vertical = state.mode.siteProfile.vertical;

  BIFactory.updateDefaults({
    market: state.mode.market || '',
    msid: state.mode.query.metaSiteId || '',
    site_id: state.mode.query.siteId || '',
    esi: state.mode.query.editorSessionId || ''
  });
}


export function initBI(state) {
  BIFactory = biLogger.factory({endpoint: 'market', host: state.mode.isProduction ? 'frog.wix.com' : 'frog.wixpress.com'});

  BIFactory.setDefaults({src: 24});

  BI = BIFactory.logger();
}


const events = {};

/**
 * Execute BI event
 * @string name
 * @object params
 * @returns Promise
 */
export function execBI(name, params = {}) {

  if (typeof events[name] === 'function') {
    const data = events[name](params);
    const defaults = {_: new Date().getTime(), vertical: appEventStore.vertical};

    if (data) {
      BI.log(Object.assign({}, defaults, data)).catch(() => {});
    }

  } else {
    console.log('BI event is not defined', name, params);
  }

  // TODO remove promise + refactor to not use this function as promise
  return Promise.resolve();
}

events.COLLECTIONS_CHANGE_OPEN = () => {
  return {
    evid: 30,
    origin: appEventStore.marketOrigin,
    category: appEventStore.vertical + '-collection'
  };
};
events.COLLECTIONS_CHANGE_CLOSE = (data) => {
  return {
    evid: 36,
    origin: appEventStore.marketOrigin,
    category: appEventStore.vertical + '-collection',
    closing_method: data.closing_method
  };
};

events.COLLECTIONS_VERTICAL_SELECTED = (data) => {
  appEventStore.vertical = data.vertical;
  return {
    evid: 40,
    origin_category: data.oldVertical + '-collection',
    category: data.vertical + '-collection',
    origin: appEventStore.marketOrigin,
    vertical: data.vertical
  };
};

events.SITE_PROFILE_CHANGED = (data) => {
  return {evid: 45, vertical: data.vertical, cat: data.oldVertical};
};

events.COLLECTIONS_OPEN = () => {
  return {
    evid: 20,
    origin: appEventStore.marketOrigin,
    category: appEventStore.vertical + '-collection'
  };
};

events.COLLECTIONS_CLOSE = () => {
  return {
    evid: 16,
    origin: appEventStore.marketOrigin,
    category: appEventStore.vertical + '-collection'
  };
};

events.COLLECTIONS_LOADED = (apps) => {
  if (apps.length === 0) {
    return;
  }
  const data = {evid: 58};
  const skip = apps.length === 5 ? -1 : -2;

  for (let i = 1; i <= 5; i++) {
    data[`app_id${i}`] = apps[i + skip] ? apps[i + skip].appDefinitionId : '';
  }
  return data;
};

events.APP_MARKET_OPENED = () => {
  if (appEventStore.isCurrentNonModal && !events.APP_MARKET_OPENED.fired) {
    events.APP_MARKET_OPENED.fired = true;
    const name = `oneAppMarket-visited-${appEventStore.urlParams.metaSiteId}`;
    const isFirst = !safeLocalStorage.get(name);
    safeLocalStorage.set(name, true);

    return {
      evid: 11,
      num_pending_apps: 0, // TODO remove this field when only this market will be open
      referral_info: appEventStore.urlParams.referralInfo,
      referral_info_category: appEventStore.urlParams.referralInfoCategory,
      personalized: appEventStore.vertical,
      page_name: appEventStore.lastNonModalStateName,
      is_first: isFirst,
      ts: getPageLoadTime(),
      ts_partial: getPageRenderingTime()
    };
  }
};

events.SEARCH_INPUT_FOCUS = () => {
  return {evid: 14};
};

events.SEARCH_INPUT_SUBMIT = (data) => {
  return {
    evid: 13,
    search_term: data.search_term,
    origin: data.origin
  };
};

events.AUTO_COMPLETE_SUBMIT = (data) => {
  if (data.type === 'app') {
    appEventStore.lastNonModalStateName = 'search_auto';
  }

  return Object.assign({evid: 29}, data);
};

events.UPDATE_AUTO_COMPLETE_QUERY = (data) => {
  appEventStore.autoCompleteQuery = data.query;
  appEventStore.autocomplete = data; // For AM 2.0
};

events.ADD_APP_CLICK = (data) => {
  appEventStore.lastAppClickedPosition = data.biPosition;
  appEventStore.addAppFlow = {app: data.app, fromModal: lastLocalStateKey !== appEventStore.lastNonModalStateKey};

  return {
    evid: 31,
    component_type: data.app.appType,
    item_position: data.biPosition,
    app_id: data.app.appDefinitionId,
    is_first: !data.app.isInstalled,
    has_permissions: data.hasPermissions,
    referral_info: appEventStore.urlParams.referralInfo,
    referral_info_category: appEventStore.urlParams.referralInfoCategory,
    target: appEventStore.market,
    modal_tab_name: appEventStore.addAppFlow.fromModal ? appEventStore.lastModalTabId : undefined,
    addition_method: appEventStore.addAppFlow.fromModal ? 'modal' : 'direct_add',
    category: getPageNameForAppBox(),
    search_term: getQueryIfLastInSearch()
  };
};

events.ADD_BUSINESS_APP_FLOW = (data) => {
  appEventStore.lastAppClickedPosition = data.biPosition;
  appEventStore.addAppFlow = {app: data.app, fromModal: lastLocalStateKey !== appEventStore.lastNonModalStateKey};

  return {
    evid: 32,
    app_id: data.app.appDefinitionId,
    addition_method: appEventStore.addAppFlow.fromModal ? 'modal' : 'direct_add',
    category: getPageNameForAppBox(),
    component_type: data.app.appType,
    is_first: !data.app.isInstalled,
    item_position: data.biPosition,
    modal_tab_name: appEventStore.addAppFlow.fromModal ? appEventStore.lastModalTabId : undefined,
    referral_info: appEventStore.urlParams.referralInfo,
    referral_info_category: appEventStore.urlParams.referralInfoCategory,
    search_term: getQueryIfLastInSearch()
  };
};

events.OPEN_APP_CLICK = (data) => {
  appEventStore.lastAppClickedPosition = data.biPosition;
  appEventStore.addAppFlow = {app: data.app, fromModal: lastLocalStateKey !== appEventStore.lastNonModalStateKey};

  return {
    evid: 33,
    item_position: data.biPosition,
    app_id: data.app.appDefinitionId,
    referral_info: appEventStore.urlParams.referralInfo,
    target: appEventStore.market,
    method: appEventStore.addAppFlow.fromModal ? 'modal' : 'direct_open',
    category: getPageNameForAppBox(),
    search_term: getQueryIfLastInSearch()
  };
};

events.APP_MODAL_OPEN_CLICK = (data) => {
  appEventStore.lastAppClickedPosition = data.biPosition;
  appEventStore.modalOpenedManually = true;
  appEventStore.lastStateKey = 'app-modal';
  return {
    evid: 21,
    item_position: data.biPosition,
    app_id: data.app.appDefinitionId,
    referral_info: appEventStore.urlParams.referralInfo,
    referral_info_category: appEventStore.urlParams.referralInfoCategory,
    section: appEventStore.urlParams.section,
    category: data.category || getPageNameForAppBox(),
    component_type: data.app.appType,
    search_term: getQueryIfLastInSearch()
  };
};


events.CATEGORY_LINK_CLICK = (data) => {
  return {
    evid: 12,
    origin: data.origin,
    category: data.category
  };
};

events.SEARCH_RESULTS_LOADED = (data) => {
  appEventStore.lastSearch = {query: data.search_term, count: data.num_results, firstAppDefinitionId: data.first_result_app_id};
  appEventStore.lastNonModalStateName = data.num_results ? 'search' : 'search_no_result';

  if (!data.skipBI) {
    return {
      evid: 15,
      search_term: data.search_term,
      num_results: data.num_results,
      first_result_app_id: data.first_result_app_id
    };
  }
};

events.CATEGORIES_POPUP_LINK_CLICK = () => {
  return {
    evid: 101,
    page_id: appEventStore.lastNonModalStateName
  };
};

events.BACK_LINK_CLICK = () => {
  return {
    evid: 102,
    source_page: appEventStore.lastNonModalStateName,
    target_page: 'home'
  };
};

events.CATEGORY_HOVER = (data) => {
  return {evid: 103, category: data.slug};
};

events.WISH_LIST_CLICK = () => {
  return {
    evid: 51,
    num_results: appEventStore.lastSearch.count,
    origin: appEventStore.lastSearch.count ? 'search' : 'search_no_result',
    search_term: appEventStore.lastSearch.query
  };
};

events.WISH_LIST_LOADED = (data) => {
  return {
    evid: 52,
    num_results: appEventStore.lastSearch.count,
    origin: data.success ? 'success_message' : (appEventStore.lastSearch.count ? 'search' : 'search_no_result'),
    search_term: appEventStore.lastSearch.query
  };
};

events.WISH_LIST_SEND_CLICK = (data) => {
  return {
    evid: 54,
    num_results: appEventStore.lastSearch.count,
    origin: data.success ? 'success_message' : (appEventStore.lastSearch.count ? 'search' : 'search_no_result'),
    search_term: appEventStore.lastSearch.query
  };
};

events.WISH_LIST_SEND_SUCCESS = (data) => {
  return {
    evid: 55,
    description: data.description,
    num_results: appEventStore.lastSearch.count,
    origin: data.success ? 'success_message' : (appEventStore.lastSearch.count ? 'search' : 'search_no_result'),
    search_term: appEventStore.lastSearch.query,
    title: data.title
  };
};

events.WISH_LIST_CLOSE = (data) => {
  return {evid: 53, closing_method: data.closing_method};
};
events.WISH_LIST_CLOSE_ON_SUCCESS = (data) => {
  return {evid: 56, closing_method: data.closing_method};
};
events.WISH_LIST_ANOTHER_CLICK = () => {
  return {evid: 57};
};

events.PENDING_REMOVE_CLICK = (data) => {
  return {evid: 61, app_id: data.app_id};
};
events.PENDING_REMOVE_DONE = (data) => {
  return {evid: 62, app_id: data.app_id};
};


events.APP_PAGE_OPENED = (data) => {
  appEventStore.modalApp = data.app;

  if (!events.APP_PAGE_OPENED.fired) {
    events.APP_PAGE_OPENED.fired = true;

    return {
      evid: 22,
      app_id: data.app.appDefinitionId,
      category: appEventStore.lastNonModalStateName,
      component_type: data.app.appType,
      item_position: data.biPosition,
      referral_info: appEventStore.urlParams.referralInfo,
      referral_info_category: appEventStore.urlParams.referralInfoCategory,
      search_term: getQueryIfLastInSearch(),
      section: appEventStore.urlParams.section,
      ts: getPageLoadTime()
    };
  }
};

events.APP_MODAL_TAB_CLICK = (data) => {
  appEventStore.lastModalTabId = data.tabId;
  return {evid: 23, app_id: appEventStore.modalApp.appDefinitionId, tab_name: data.tabId};
};

events.APP_MODAL_DEV_NAME_CLICK = (data) => {
  return {evid: 24, app_id: appEventStore.modalApp.appDefinitionId, origin: data.origin};
};

events.APP_MODAL_SUPPORT_INFO_CLICK = (data) => {
  return {evid: 25, app_id: appEventStore.modalApp.appDefinitionId, info_type: data.infoType};
};

events.APP_MODAL_CAROUSEL_CLICK = (data) => {
  return {evid: 26, app_id: appEventStore.modalApp.appDefinitionId, thumbnail_type: data.type};
};

events.PERMISSIONS_MODAL_OPEN = (data) => {
  appEventStore.permissionsApp = data.app;
  const message_type = data.app.listedInMarket ? 'default' : 'unlisted_app_warning';
  return {evid: 41, app_id: data.app.appDefinitionId, message_type};
};

events.PERMISSIONS_MODAL_CLOSE = (data) => {
  const message_type = appEventStore.permissionsApp.listedInMarket ? 'default' : 'unlisted_app_warning';
  return {evid: 42, app_id: appEventStore.permissionsApp.appDefinitionId, closing_method: data.method, message_type};
};

events.PERMISSIONS_MODAL_AGREE = () => {
  const message_type = appEventStore.permissionsApp.listedInMarket ? 'default' : 'unlisted_app_warning';
  return {evid: 43, app_id: appEventStore.permissionsApp.appDefinitionId, message_type};
};

events.PERMISSIONS_MODAL_TERMS = () => {
  return {evid: 44, app_id: appEventStore.permissionsApp.appDefinitionId};
};

events.UNIFICATION_PROVISION_OPEN = (data) => {
  return {evid: 90, app_id: data.app.appDefinitionId};
};

events.UNIFICATION_PROVISION_CLOSE = (data) => {
  return {evid: 92, app_id: data.app.appDefinitionId, closing_method: data.closing_method};
};

events.UNIFICATION_PROVISION_GO_TO_CLICK = (data) => {
  return {
    evid: 93,
    app_id: data.app.appDefinitionId,
    go_to_market: 'dashboard',
    origin: data.origin || appEventStore.lastNonModalStateName
  };
};

events.SOCIAL_SHARE = (data) => {
  return {
    evid: 27,
    app_id: data.appDefinitionId,
    share_type: data.type,
  };
};

events.ERROR_PAGE_LOADED = (data) => {
  return {
    evid: 200,
    state: data.url,
    site_id: appEventStore.urlParams.siteId || '',
    referral_info: appEventStore.urlParams.referralInfo
  };
};


function getPageName(pathname, state) {
  // TODO when category page url will be /categories/music - update this function
  const pageKey = getPageKey(pathname);

  switch (pageKey) {
    case 'pending':
      return 'pending-apps';

    case 'developer':
      return 'developer-apps';

    case 'open-apps':
      return 'single-app';

    case 'search':
      return state.search.apps.length ? 'search' : 'search_no_result';

    case 'home':
      return 'home';

    default:
      return isModal(pathname) ? null : pageKey; // category page otherwise
  }
}

function getPageKey(pathname) {
  return pathname.split('/')[1] || 'home';
}

function isModal(pathname) {
  const reg = /(app-modal|permissions-modal|provision-modal|\/overview|\/demo|\/features|\/reviews|\/developer)/i;
  return reg.test(pathname);
}

function getPageNameForAppBox() {
  if (appEventStore.lastNonModalStateName === 'home') {
    return `${appEventStore.vertical}-collection`;
  } else {
    return appEventStore.lastNonModalStateName;
  }
}

function getQueryIfLastInSearch() {
  if (appEventStore.lastNonModalStateName === 'search' || appEventStore.lastNonModalStateName === 'search_no_result') {
    return appEventStore.lastSearch.query;
  }
  if (appEventStore.lastNonModalStateName === 'search_auto') {
    return appEventStore.autoCompleteQuery;
  }

  return '';
}

export function getBIForEditor() {
  return {
    category: appEventStore.lastNonModalStateName,
    section: appEventStore.urlParams.section,
    'adding_method': appEventStore.addAppFlow.fromModal ? 'modal' : 'direct_add',
    'search_term': getQueryIfLastInSearch(),
    'item_position': appEventStore.lastAppClickedPosition,
    'modal_tab_name': appEventStore.addAppFlow.fromModal ? appEventStore.lastModalTabId : undefined,
    'referral_info_category': appEventStore.urlParams.referralInfoCategory || undefined,
    'referral_info': appEventStore.urlParams.referralInfo || undefined
  };
}


// BI.log({evtId: 1});
// BI.log('EVT-1');
// BI.log('EVT-1', {exId: 12});
// BIFactory.setEvents();

//http://frog.wix.com/market?_=1474794037586&src=24&market=editor&msid=ea2d7a63-3c77-47db-adf9-723f318a6914&site_id=7ff1943d-261f-4136-a87c-71f983ebb72c&ownerId=undefined&roles=&vertical=online-store&evid=11&num_pending_apps=1&referral_info=market_button&referral_info_category=undefined&personalized=online-store



// WEBPACK FOOTER //
// ./services/bi-events-manager.js